/**
 * Reader comments on published story maps.
 *
 * Two surfaces over the same rows:
 * - the share link (`/api/story-maps/shared/<slug>/comments`): readers of the
 *   published story, anonymous or signed in. Plain fetch, like usePublicStory,
 *   so an anonymous 401/404 never trips the global logout; credentials go
 *   along so a signed-in organisation member is recognised. The anonymous
 *   comment key travels in the query string (reads) or JSON body (writes),
 *   never a custom header: the API's CORS allow-list only has the standard
 *   headers, and a custom one fails the browser's preflight silently;
 * - the builder (`/api/projects/<id>/story-maps/<id>/comments`): the story's
 *   own organisation, through the shared api wrapper.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";

import { api } from "../utils/api";
import { getApiBase } from "../utils/apiBase";
import { ApiError, ApiResponse } from "../types/api";
import { StoryComment } from "../types/storyMap";

// ---------------------------------------------------------------------------
// Anonymous identity
// ---------------------------------------------------------------------------

const COMMENT_KEY_STORAGE = "storyCommentKey";
const COMMENT_NAME_STORAGE = "storyCommentName";

const safeGet = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode / blocked storage: the key just won't survive a reload */
  }
};

/**
 * The opaque per-browser token an anonymous reader posts comments under. It
 * is what lets them resolve or delete their own thread later, so it is minted
 * once and kept. Not a secret of any value beyond that.
 */
export const getCommentAuthorKey = (): string => {
  const existing = safeGet(COMMENT_KEY_STORAGE);
  if (existing) return existing;
  const fresh =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  safeSet(COMMENT_KEY_STORAGE, fresh);
  return fresh;
};

export const getRememberedCommentName = (): string => safeGet(COMMENT_NAME_STORAGE) ?? "";
export const rememberCommentName = (name: string) => safeSet(COMMENT_NAME_STORAGE, name);

// ---------------------------------------------------------------------------
// Share-link surface
// ---------------------------------------------------------------------------

const publicCommentsKey = (slug: string | undefined) => ["publicStoryComments", slug];

async function publicRequest<T>(
  path: string,
  init: { method?: string; body?: unknown } = {}
): Promise<T> {
  const method = init.method ?? "GET";
  const headers: Record<string, string> = {};
  if (method !== "GET") {
    headers["Content-Type"] = "application/json";
    // SessionAuthentication enforces CSRF for signed-in readers only; for
    // anonymous ones the cookie is simply absent and the header harmless.
    headers["X-CSRFToken"] = Cookies.get("csrftoken") || "";
  }
  const response = await fetch(`${getApiBase()}${path}`, {
    method,
    headers,
    credentials: "include",
    body: method === "GET" ? undefined : JSON.stringify(init.body ?? {}),
  });
  if (!response.ok) {
    let message = "Request failed";
    try {
      const payload = await response.json();
      message = payload?.message || payload?.error || message;
    } catch {
      /* non-JSON error body */
    }
    if (response.status === 429) {
      message = "Too many requests from this connection. Please wait a little and try again.";
    }
    throw { message, status: response.status } as ApiError;
  }
  if (response.status === 204) return undefined as T;
  return ((await response.json()) as ApiResponse<T>).data;
}

export const usePublicStoryComments = (slug: string | undefined, enabled: boolean) =>
  useQuery<StoryComment[], ApiError>({
    queryKey: publicCommentsKey(slug),
    queryFn: () =>
      publicRequest<StoryComment[]>(
        `/api/story-maps/shared/${slug}/comments?author_key=${encodeURIComponent(
          getCommentAuthorKey()
        )}`
      ),
    enabled: !!slug && enabled,
    staleTime: 30_000,
  });

export interface CreateCommentInput {
  body: string;
  /** Required for a thread opener; ignored for a reply (inherits the thread's scene). */
  sceneId?: string;
  parentId?: string;
  /** Anonymous readers only; signed-in readers post under their account name. */
  authorName?: string;
}

/** Create / resolve / delete through the share link. */
export const usePublicCommentActions = (slug: string | undefined) => {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: publicCommentsKey(slug) });
  const authorKey = getCommentAuthorKey();

  const create = useMutation<StoryComment, ApiError, CreateCommentInput>({
    mutationFn: (input) =>
      publicRequest<StoryComment>(`/api/story-maps/shared/${slug}/comments`, {
        method: "POST",
        body: {
          body: input.body,
          scene_id: input.sceneId,
          parent_id: input.parentId,
          author_name: input.authorName,
          author_key: authorKey,
        },
      }),
    onSuccess: (_, input) => {
      if (input.authorName) rememberCommentName(input.authorName);
      invalidate();
    },
  });

  const resolve = useMutation<StoryComment, ApiError, { id: string; resolved: boolean }>({
    mutationFn: ({ id, resolved }) =>
      publicRequest<StoryComment>(`/api/story-maps/shared/${slug}/comments/${id}`, {
        method: "PATCH",
        body: { resolved, author_key: authorKey },
      }),
    onSuccess: invalidate,
  });

  const remove = useMutation<void, ApiError, string>({
    mutationFn: (id) =>
      publicRequest<void>(`/api/story-maps/shared/${slug}/comments/${id}`, {
        method: "DELETE",
        body: { author_key: authorKey },
      }),
    onSuccess: invalidate,
  });

  return { create, resolve, remove };
};

// ---------------------------------------------------------------------------
// Builder surface
// ---------------------------------------------------------------------------

const storyCommentsKey = (storyMapId: string | undefined) => ["storyMapComments", storyMapId];

export const useStoryMapComments = (storyMapId: string | undefined) =>
  useQuery<ApiResponse<StoryComment[]>, ApiError>({
    queryKey: storyCommentsKey(storyMapId),
    queryFn: () =>
      api.request<ApiResponse<StoryComment[]>>(`/api/story-maps/${storyMapId}/comments`),
    enabled: !!storyMapId,
    // Readers comment while the author has the builder open; keep the
    // panel reasonably fresh without hammering the API.
    refetchInterval: 60_000,
  });

export const useStoryMapCommentActions = (storyMapId: string | undefined) => {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: storyCommentsKey(storyMapId) });
  const base = `/api/story-maps/${storyMapId}/comments`;

  const create = useMutation<ApiResponse<StoryComment>, ApiError, CreateCommentInput>({
    mutationFn: (input) =>
      api.request<ApiResponse<StoryComment>>(base, {
        method: "POST",
        body: { body: input.body, scene_id: input.sceneId, parent_id: input.parentId },
      }),
    onSuccess: invalidate,
  });

  const resolve = useMutation<
    ApiResponse<StoryComment>,
    ApiError,
    { id: string; resolved: boolean }
  >({
    mutationFn: ({ id, resolved }) =>
      api.request<ApiResponse<StoryComment>>(`${base}/${id}`, {
        method: "PATCH",
        body: { resolved },
      }),
    onSuccess: invalidate,
  });

  const remove = useMutation<void, ApiError, string>({
    mutationFn: (id) => api.request<void>(`${base}/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });

  return { create, resolve, remove };
};
